import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { adminAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Switch } from '../components/ui/switch';
import {
  ArrowLeft, Plus, Trash2, Edit2, Users, Cpu, Coins, Activity,
  Terminal, Shield, DollarSign, BarChart3, Save, Gauge, Clock,
  FolderOpen, TrendingUp, X
} from 'lucide-react';
import { toast } from 'sonner';

export default function AdminPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tab, setTab] = useState('analytics');
  const [providers, setProviders] = useState([]);
  const [rates, setRates] = useState([]);
  const [users, setUsers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);

  // Dialog states
  const [showProviderDialog, setShowProviderDialog] = useState(false);
  const [showRateDialog, setShowRateDialog] = useState(false);
  const [editingProvider, setEditingProvider] = useState(null);
  const [editingRate, setEditingRate] = useState(null);

  // Form states
  const [providerForm, setProviderForm] = useState({ name: '', provider_key: '', models: '', is_active: true });
  const [rateForm, setRateForm] = useState({ action: '', tokens_cost: 0, description: '' });

  // New tabs state
  const [quotas, setQuotas] = useState({ default_quota: 10000, overrides: [] });
  const [timeSavedRates, setTimeSavedRates] = useState({ rates: {}, defaults: {} });
  const [analytics, setAnalytics] = useState(null);
  const [allProjects, setAllProjects] = useState([]);
  const [activityUser, setActivityUser] = useState(null);
  const [activityData, setActivityData] = useState(null);
  const [defaultQuotaInput, setDefaultQuotaInput] = useState('');
  const [editingRates, setEditingRates] = useState(null);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    try {
      const [p, r, u, l, s, q, t, a, ap] = await Promise.all([
        adminAPI.providers(), adminAPI.rates(), adminAPI.users(), adminAPI.logs(), adminAPI.stats(),
        adminAPI.quotas(), adminAPI.timeSavedRates(), adminAPI.analytics(), adminAPI.allProjects(),
      ]);
      setProviders(p.data);
      setRates(r.data);
      setUsers(u.data);
      setLogs(l.data);
      setStats(s.data);
      setQuotas(q.data);
      setDefaultQuotaInput(String(q.data.default_quota));
      setTimeSavedRates(t.data);
      setEditingRates(t.data.rates);
      setAnalytics(a.data);
      setAllProjects(ap.data);
    } catch (err) {
      toast.error('Failed to load admin data');
    }
  };

  const saveDefaultQuota = async () => {
    try {
      await adminAPI.setDefaultQuota(parseInt(defaultQuotaInput) || 0);
      toast.success('Default quota updated');
      const q = await adminAPI.quotas();
      setQuotas(q.data);
    } catch { toast.error('Failed'); }
  };

  const setUserQuota = async (userId, value) => {
    try {
      const v = value === '' || value === null ? null : parseInt(value);
      await adminAPI.setUserQuota(userId, v);
      toast.success(v === null ? 'Override removed' : 'Quota set');
      const q = await adminAPI.quotas();
      setQuotas(q.data);
    } catch { toast.error('Failed'); }
  };

  const saveTimeSavedRates = async () => {
    try {
      await adminAPI.setTimeSavedRates(editingRates);
      toast.success('Time-saved rates updated');
      const t = await adminAPI.timeSavedRates();
      setTimeSavedRates(t.data);
      setEditingRates(t.data.rates);
    } catch { toast.error('Failed'); }
  };

  const viewActivity = async (u) => {
    try {
      setActivityUser(u);
      const res = await adminAPI.userActivity(u.id);
      setActivityData(res.data);
    } catch { toast.error('Failed to load activity'); }
  };

  // Provider CRUD
  const openProviderDialog = (provider = null) => {
    if (provider) {
      setEditingProvider(provider);
      setProviderForm({ name: provider.name, provider_key: provider.provider_key, models: provider.models.join(', '), is_active: provider.is_active });
    } else {
      setEditingProvider(null);
      setProviderForm({ name: '', provider_key: '', models: '', is_active: true });
    }
    setShowProviderDialog(true);
  };

  const saveProvider = async () => {
    const data = { ...providerForm, models: providerForm.models.split(',').map(m => m.trim()).filter(Boolean) };
    try {
      if (editingProvider) {
        await adminAPI.updateProvider(editingProvider.id, data);
        toast.success('Provider updated');
      } else {
        await adminAPI.createProvider(data);
        toast.success('Provider created');
      }
      setShowProviderDialog(false);
      loadAll();
    } catch (err) { toast.error('Failed to save provider'); }
  };

  const deleteProvider = async (id) => {
    try { await adminAPI.deleteProvider(id); toast.success('Deleted'); loadAll(); } catch { toast.error('Failed'); }
  };

  // Rate CRUD
  const openRateDialog = (rate = null) => {
    if (rate) {
      setEditingRate(rate);
      setRateForm({ action: rate.action, tokens_cost: rate.tokens_cost, description: rate.description });
    } else {
      setEditingRate(null);
      setRateForm({ action: '', tokens_cost: 0, description: '' });
    }
    setShowRateDialog(true);
  };

  const saveRate = async () => {
    try {
      if (editingRate) {
        await adminAPI.updateRate(editingRate.id, rateForm);
        toast.success('Rate updated');
      } else {
        await adminAPI.createRate(rateForm);
        toast.success('Rate created');
      }
      setShowRateDialog(false);
      loadAll();
    } catch { toast.error('Failed to save rate'); }
  };

  const deleteRate = async (id) => {
    try { await adminAPI.deleteRate(id); toast.success('Deleted'); loadAll(); } catch { toast.error('Failed'); }
  };

  const updateUserTokens = async (userId, amount) => {
    try { await adminAPI.updateUserTokens(userId, amount); toast.success('Tokens updated'); loadAll(); } catch { toast.error('Failed'); }
  };

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="admin-page">
      <nav className="sticky top-0 z-50 backdrop-blur-xl bg-black/40 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 h-14 flex items-center">
          <Button variant="ghost" size="sm" className="text-white/50 hover:text-white" onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="w-4 h-4 mr-1.5" /> Dashboard
          </Button>
          <div className="ml-4 flex items-center gap-2">
            <Shield className="w-4 h-4 text-indigo-400" />
            <span className="text-sm font-semibold text-white">Admin Panel</span>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            {[
              { label: 'Users', value: stats.total_users, icon: Users, color: 'text-indigo-400' },
              { label: 'Projects', value: stats.total_projects, icon: Terminal, color: 'text-cyan-400' },
              { label: 'Generations', value: stats.total_generations, icon: Activity, color: 'text-emerald-400' },
              { label: 'Tokens Used', value: stats.total_tokens_consumed, icon: Coins, color: 'text-amber-400' },
              { label: 'Revenue', value: `$${(stats.total_revenue || 0).toFixed(2)}`, icon: DollarSign, color: 'text-green-400' },
            ].map((s, i) => (
              <Card key={i} className="bg-white/[0.03] border-white/10" data-testid={`admin-stat-${i}`}>
                <CardContent className="p-4">
                  <s.icon className={`w-5 h-5 ${s.color} mb-2`} />
                  <p className="text-2xl font-bold text-white">{s.value}</p>
                  <p className="text-[10px] tracking-wider uppercase text-white/30">{s.label}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-white/5 border border-white/10 mb-6 flex-wrap h-auto">
            <TabsTrigger value="analytics" className="data-[state=active]:bg-indigo-600/20 data-[state=active]:text-indigo-400 text-white/40" data-testid="admin-tab-analytics">Analytics</TabsTrigger>
            <TabsTrigger value="providers" className="data-[state=active]:bg-indigo-600/20 data-[state=active]:text-indigo-400 text-white/40" data-testid="admin-tab-providers">LLM Providers</TabsTrigger>
            <TabsTrigger value="rates" className="data-[state=active]:bg-indigo-600/20 data-[state=active]:text-indigo-400 text-white/40" data-testid="admin-tab-rates">Token Rates</TabsTrigger>
            <TabsTrigger value="quotas" className="data-[state=active]:bg-indigo-600/20 data-[state=active]:text-indigo-400 text-white/40" data-testid="admin-tab-quotas">Quotas</TabsTrigger>
            <TabsTrigger value="time-saved" className="data-[state=active]:bg-indigo-600/20 data-[state=active]:text-indigo-400 text-white/40" data-testid="admin-tab-time-saved">Time-Saved Rates</TabsTrigger>
            <TabsTrigger value="users" className="data-[state=active]:bg-indigo-600/20 data-[state=active]:text-indigo-400 text-white/40" data-testid="admin-tab-users">Users</TabsTrigger>
            <TabsTrigger value="all-projects" className="data-[state=active]:bg-indigo-600/20 data-[state=active]:text-indigo-400 text-white/40" data-testid="admin-tab-all-projects">All Projects</TabsTrigger>
            <TabsTrigger value="logs" className="data-[state=active]:bg-indigo-600/20 data-[state=active]:text-indigo-400 text-white/40" data-testid="admin-tab-logs">Logs</TabsTrigger>
          </TabsList>

          {/* Providers Tab */}
          <TabsContent value="providers">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-white" style={{fontFamily: 'Space Grotesk, sans-serif'}}>LLM Providers</h2>
              <Button className="bg-indigo-600 hover:bg-indigo-500 text-white" onClick={() => openProviderDialog()} data-testid="add-provider-btn">
                <Plus className="w-4 h-4 mr-1.5" /> Add Provider
              </Button>
            </div>
            <Card className="bg-white/[0.03] border-white/10">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="text-white/40">Name</TableHead>
                    <TableHead className="text-white/40">Key</TableHead>
                    <TableHead className="text-white/40">Models</TableHead>
                    <TableHead className="text-white/40">Status</TableHead>
                    <TableHead className="text-white/40 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {providers.map((p) => (
                    <TableRow key={p.id} className="border-white/10 hover:bg-white/[0.02]" data-testid={`provider-row-${p.id}`}>
                      <TableCell className="text-white font-medium">{p.name}</TableCell>
                      <TableCell className="text-white/60 font-mono text-xs">{p.provider_key}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {p.models.slice(0, 3).map(m => (
                            <Badge key={m} variant="outline" className="text-white/40 border-white/10 text-[10px]">{m}</Badge>
                          ))}
                          {p.models.length > 3 && <Badge variant="outline" className="text-white/30 border-white/10 text-[10px]">+{p.models.length - 3}</Badge>}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={p.is_active ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-red-500/10 text-red-400 border-red-500/20'}>
                          {p.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="icon" className="text-white/30 hover:text-white h-8 w-8" onClick={() => openProviderDialog(p)} data-testid={`edit-provider-${p.id}`}>
                          <Edit2 className="w-3.5 h-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="text-red-400/50 hover:text-red-400 h-8 w-8" onClick={() => deleteProvider(p.id)} data-testid={`delete-provider-${p.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          {/* Rates Tab */}
          <TabsContent value="rates">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-white" style={{fontFamily: 'Space Grotesk, sans-serif'}}>Token Rates</h2>
              <Button className="bg-indigo-600 hover:bg-indigo-500 text-white" onClick={() => openRateDialog()} data-testid="add-rate-btn">
                <Plus className="w-4 h-4 mr-1.5" /> Add Rate
              </Button>
            </div>
            <Card className="bg-white/[0.03] border-white/10">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="text-white/40">Action</TableHead>
                    <TableHead className="text-white/40">Cost (tokens)</TableHead>
                    <TableHead className="text-white/40">Description</TableHead>
                    <TableHead className="text-white/40 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rates.map((r) => (
                    <TableRow key={r.id} className="border-white/10 hover:bg-white/[0.02]" data-testid={`rate-row-${r.id}`}>
                      <TableCell className="text-white font-medium font-mono text-sm">{r.action}</TableCell>
                      <TableCell>
                        <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20">{r.tokens_cost} tokens</Badge>
                      </TableCell>
                      <TableCell className="text-white/40 text-sm">{r.description}</TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="icon" className="text-white/30 hover:text-white h-8 w-8" onClick={() => openRateDialog(r)}>
                          <Edit2 className="w-3.5 h-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="text-red-400/50 hover:text-red-400 h-8 w-8" onClick={() => deleteRate(r.id)}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <h2 className="text-xl font-semibold text-white mb-4" style={{fontFamily: 'Space Grotesk, sans-serif'}}>Users</h2>
            <Card className="bg-white/[0.03] border-white/10">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="text-white/40">Name</TableHead>
                    <TableHead className="text-white/40">Email</TableHead>
                    <TableHead className="text-white/40">Role</TableHead>
                    <TableHead className="text-white/40">Tokens</TableHead>
                    <TableHead className="text-white/40">Joined</TableHead>
                    <TableHead className="text-white/40 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((u) => (
                    <TableRow key={u.id} className="border-white/10 hover:bg-white/[0.02]" data-testid={`user-row-${u.id}`}>
                      <TableCell className="text-white font-medium">{u.name}</TableCell>
                      <TableCell className="text-white/60 text-sm">{u.email}</TableCell>
                      <TableCell>
                        <Badge className={u.is_admin ? 'bg-indigo-500/10 text-indigo-400 border-indigo-500/20' : 'bg-white/5 text-white/40 border-white/10'}>
                          {u.is_admin ? 'Admin' : 'User'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-amber-400 font-mono text-sm">{u.token_balance}</TableCell>
                      <TableCell className="text-white/30 text-sm">{new Date(u.created_at).toLocaleDateString()}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="sm" className="h-7 text-xs text-indigo-400 hover:text-indigo-300" onClick={() => viewActivity(u)} data-testid={`view-activity-${u.id}`}>
                            <Activity className="w-3 h-3 mr-1" /> Activity
                          </Button>
                          <Input
                            type="number"
                            defaultValue={u.token_balance}
                            className="bg-black/50 border-white/10 text-white w-24 h-7 text-xs"
                            onBlur={(e) => {
                              const val = parseInt(e.target.value);
                              if (!isNaN(val) && val !== u.token_balance) updateUserTokens(u.id, val);
                            }}
                            data-testid={`user-tokens-input-${u.id}`}
                          />
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          {/* Logs Tab */}
          <TabsContent value="logs">
            <h2 className="text-xl font-semibold text-white mb-4" style={{fontFamily: 'Space Grotesk, sans-serif'}}>Generation Logs</h2>
            <Card className="bg-white/[0.03] border-white/10">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="text-white/40">Time</TableHead>
                    <TableHead className="text-white/40">User</TableHead>
                    <TableHead className="text-white/40">Action</TableHead>
                    <TableHead className="text-white/40">Phase</TableHead>
                    <TableHead className="text-white/40">Tokens</TableHead>
                    <TableHead className="text-white/40">Preview</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.slice(0, 50).map((log) => (
                    <TableRow key={log.id} className="border-white/10 hover:bg-white/[0.02]" data-testid={`log-row-${log.id}`}>
                      <TableCell className="text-white/30 text-xs">{new Date(log.created_at).toLocaleString()}</TableCell>
                      <TableCell className="text-white/60 text-sm font-mono">{log.user_id?.slice(0, 8)}...</TableCell>
                      <TableCell><Badge variant="outline" className="text-white/40 border-white/10 text-[10px]">{log.action}</Badge></TableCell>
                      <TableCell className="text-white/40 text-sm">{log.phase}</TableCell>
                      <TableCell className="text-amber-400 text-sm">{log.tokens_spent}</TableCell>
                      <TableCell className="text-white/30 text-xs max-w-[200px] truncate">{log.message_preview}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics">
            <h2 className="text-xl font-semibold text-white mb-4" style={{fontFamily: 'Space Grotesk, sans-serif'}}>Platform Analytics</h2>
            {!analytics ? (
              <Card className="bg-white/[0.03] border-white/10"><CardContent className="p-8 text-center text-white/40">Loading analytics...</CardContent></Card>
            ) : (
              <div className="space-y-6">
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  {[
                    { label: 'Active 7d', value: analytics.active_users_7d, icon: Users, color: 'text-indigo-400' },
                    { label: 'Active 30d', value: analytics.active_users_30d, icon: Users, color: 'text-cyan-400' },
                    { label: 'Projects 7d', value: analytics.projects_last_7d, icon: FolderOpen, color: 'text-emerald-400' },
                    { label: 'Projects 30d', value: analytics.projects_last_30d, icon: FolderOpen, color: 'text-green-400' },
                    { label: 'Hours Saved', value: analytics.total_time_saved_hours, icon: Clock, color: 'text-amber-400' },
                  ].map((s, i) => (
                    <Card key={i} className="bg-white/[0.03] border-white/10" data-testid={`analytics-stat-${i}`}>
                      <CardContent className="p-4">
                        <s.icon className={`w-5 h-5 ${s.color} mb-2`} />
                        <p className="text-2xl font-bold text-white">{s.value}</p>
                        <p className="text-[10px] tracking-wider uppercase text-white/30">{s.label}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Tokens per day */}
                <Card className="bg-white/[0.03] border-white/10">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base text-white flex items-center gap-2"><TrendingUp className="w-4 h-4 text-cyan-400" /> Tokens consumed (last 14 days)</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {analytics.tokens_per_day.length === 0 ? (
                      <p className="text-white/40 text-sm py-4">No activity yet</p>
                    ) : (
                      <div className="flex items-end gap-1 h-32" data-testid="tokens-chart">
                        {analytics.tokens_per_day.map((d, i) => {
                          const max = Math.max(...analytics.tokens_per_day.map(x => x.tokens), 1);
                          const h = Math.max(4, (d.tokens / max) * 120);
                          return (
                            <div key={i} className="flex-1 flex flex-col items-center gap-1" title={`${d.date}: ${d.tokens} tokens`}>
                              <div className="w-full bg-gradient-to-t from-indigo-600 to-cyan-400 rounded-t" style={{height: `${h}px`}} />
                              <span className="text-[8px] text-white/30">{d.date.slice(5)}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Action breakdown */}
                <Card className="bg-white/[0.03] border-white/10">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base text-white flex items-center gap-2"><Clock className="w-4 h-4 text-amber-400" /> Time-saved breakdown</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow className="border-white/10 hover:bg-transparent">
                          <TableHead className="text-white/40">Action</TableHead>
                          <TableHead className="text-white/40 text-right">Count</TableHead>
                          <TableHead className="text-white/40 text-right">Hours Saved</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {analytics.action_breakdown.map((a, i) => (
                          <TableRow key={i} className="border-white/10">
                            <TableCell className="text-white text-sm font-mono">{a.action}</TableCell>
                            <TableCell className="text-right text-white/60 text-sm">{a.count}</TableCell>
                            <TableCell className="text-right text-amber-400 text-sm font-semibold">{a.hours_saved}h</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>

          {/* Quotas Tab */}
          <TabsContent value="quotas">
            <h2 className="text-xl font-semibold text-white mb-4" style={{fontFamily: 'Space Grotesk, sans-serif'}}>Token Quotas</h2>

            <Card className="bg-white/[0.03] border-white/10 mb-6">
              <CardHeader className="pb-2">
                <CardTitle className="text-base text-white flex items-center gap-2"><Gauge className="w-4 h-4 text-indigo-400" /> Global default quota</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-white/50 mb-3">Applied to all users without a specific override. Rolling 30-day window. Quota is <span className="text-amber-300">soft</span> — users are warned but not blocked.</p>
                <div className="flex items-center gap-3">
                  <Input
                    type="number"
                    value={defaultQuotaInput}
                    onChange={(e) => setDefaultQuotaInput(e.target.value)}
                    className="bg-black/50 border-white/10 text-white w-40"
                    data-testid="default-quota-input"
                  />
                  <Button onClick={saveDefaultQuota} className="bg-indigo-600 hover:bg-indigo-500 text-white" data-testid="save-default-quota-btn"><Save className="w-3.5 h-3.5 mr-1.5" /> Save</Button>
                  <span className="text-xs text-white/40">Current: <span className="text-white">{quotas.default_quota} tokens / 30d</span></span>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white/[0.03] border-white/10">
              <CardHeader className="pb-2">
                <CardTitle className="text-base text-white">Per-user overrides</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10 hover:bg-transparent">
                      <TableHead className="text-white/40">User</TableHead>
                      <TableHead className="text-white/40 text-right">Override Quota</TableHead>
                      <TableHead className="text-white/40 text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((u) => {
                      const override = quotas.overrides.find(o => o.user_id === u.id);
                      return (
                        <TableRow key={u.id} className="border-white/10" data-testid={`quota-row-${u.id}`}>
                          <TableCell className="text-white text-sm">
                            <div className="font-medium">{u.name}</div>
                            <div className="text-[10px] text-white/40">{u.email}</div>
                          </TableCell>
                          <TableCell className="text-right">
                            <Input
                              type="number"
                              placeholder={`(default ${quotas.default_quota})`}
                              defaultValue={override?.quota ?? ''}
                              className="bg-black/50 border-white/10 text-white w-32 h-8 text-xs ml-auto"
                              onBlur={(e) => {
                                const v = e.target.value;
                                const cur = override?.quota ?? null;
                                if (String(cur ?? '') !== v) setUserQuota(u.id, v === '' ? null : v);
                              }}
                              data-testid={`quota-input-${u.id}`}
                            />
                          </TableCell>
                          <TableCell className="text-right">
                            {override ? (
                              <Button variant="ghost" size="sm" className="h-7 text-xs text-red-400" onClick={() => setUserQuota(u.id, null)} data-testid={`remove-quota-${u.id}`}>
                                <X className="w-3 h-3 mr-1" /> Remove
                              </Button>
                            ) : (
                              <span className="text-[10px] text-white/30">Using default</span>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Time-Saved Rates Tab */}
          <TabsContent value="time-saved">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-white" style={{fontFamily: 'Space Grotesk, sans-serif'}}>Time-Saved Rates</h2>
              <Button onClick={saveTimeSavedRates} className="bg-indigo-600 hover:bg-indigo-500 text-white" data-testid="save-time-rates-btn">
                <Save className="w-4 h-4 mr-1.5" /> Save All
              </Button>
            </div>
            <p className="text-sm text-white/50 mb-4">Hours of manual work saved per occurrence of each AI action. Used to compute "Time Saved" on All Projects and Analytics.</p>
            <Card className="bg-white/[0.03] border-white/10">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="text-white/40">Action</TableHead>
                    <TableHead className="text-white/40 text-right">Hours saved per action</TableHead>
                    <TableHead className="text-white/40 text-right">Default</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {Object.entries(timeSavedRates.defaults || {}).map(([action, defVal]) => (
                    <TableRow key={action} className="border-white/10" data-testid={`time-rate-row-${action}`}>
                      <TableCell className="text-white text-sm font-mono">{action}</TableCell>
                      <TableCell className="text-right">
                        <Input
                          type="number"
                          step="0.1"
                          value={editingRates?.[action] ?? ''}
                          onChange={(e) => setEditingRates(prev => ({ ...prev, [action]: parseFloat(e.target.value) || 0 }))}
                          className="bg-black/50 border-white/10 text-white w-28 h-8 text-xs ml-auto"
                          data-testid={`time-rate-input-${action}`}
                        />
                      </TableCell>
                      <TableCell className="text-right text-white/30 text-xs">{defVal}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          {/* All Projects Tab */}
          <TabsContent value="all-projects">
            <h2 className="text-xl font-semibold text-white mb-4" style={{fontFamily: 'Space Grotesk, sans-serif'}}>All Projects ({allProjects.length})</h2>
            <Card className="bg-white/[0.03] border-white/10">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="text-white/40">Project</TableHead>
                    <TableHead className="text-white/40">Owner</TableHead>
                    <TableHead className="text-white/40">Type</TableHead>
                    <TableHead className="text-white/40">Tags</TableHead>
                    <TableHead className="text-white/40 text-right">Tokens</TableHead>
                    <TableHead className="text-white/40 text-right">Time Saved</TableHead>
                    <TableHead className="text-white/40 text-right">Updated</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {allProjects.map((p) => (
                    <TableRow key={p.id} className="border-white/10 hover:bg-white/[0.02]" data-testid={`all-project-row-${p.id}`}>
                      <TableCell className="text-white text-sm font-medium">{p.name}</TableCell>
                      <TableCell className="text-white/60 text-xs">
                        <div>{p.owner_name}</div>
                        <div className="text-white/30 text-[10px]">{p.owner_email}</div>
                      </TableCell>
                      <TableCell><Badge variant="outline" className="text-white/40 border-white/10 text-[10px]">{p.project_type}</Badge></TableCell>
                      <TableCell>
                        <div className="flex gap-1 flex-wrap">
                          {(p.tags || []).slice(0, 3).map(t => <Badge key={t} className="text-[9px] bg-indigo-500/10 text-indigo-300 border-indigo-500/30">{t}</Badge>)}
                        </div>
                      </TableCell>
                      <TableCell className="text-right text-amber-400 text-sm">{p.total_tokens_spent || 0}</TableCell>
                      <TableCell className="text-right text-emerald-400 text-sm font-semibold">{p.time_saved_hours}h</TableCell>
                      <TableCell className="text-right text-white/30 text-xs">{new Date(p.updated_at || p.created_at).toLocaleDateString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* User Activity Dialog */}
      <Dialog open={!!activityUser} onOpenChange={(open) => { if (!open) { setActivityUser(null); setActivityData(null); } }}>
        <DialogContent className="bg-[#0a0a0a] border-white/10 text-white max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="activity-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">Activity — {activityUser?.name} ({activityUser?.email})</DialogTitle>
          </DialogHeader>
          {!activityData ? (
            <div className="py-8 text-center text-white/40">Loading...</div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-4 gap-3">
                <Card className="bg-white/5 border-white/10"><CardContent className="p-3"><p className="text-[10px] uppercase text-white/40">Tokens Used (30d)</p><p className="text-xl font-bold text-amber-400">{activityData.total_tokens_used}</p></CardContent></Card>
                <Card className="bg-white/5 border-white/10"><CardContent className="p-3"><p className="text-[10px] uppercase text-white/40">Actions</p><p className="text-xl font-bold text-white">{activityData.total_actions}</p></CardContent></Card>
                <Card className="bg-white/5 border-white/10"><CardContent className="p-3"><p className="text-[10px] uppercase text-white/40">Projects</p><p className="text-xl font-bold text-white">{activityData.project_count}</p></CardContent></Card>
                <Card className="bg-white/5 border-white/10"><CardContent className="p-3"><p className="text-[10px] uppercase text-white/40">Quota{activityData.has_quota_override ? ' (override)' : ''}</p><p className="text-xl font-bold text-indigo-400">{activityData.quota}</p></CardContent></Card>
              </div>
              <div>
                <p className="text-sm font-medium text-white/80 mb-2">Recent activity</p>
                <div className="max-h-64 overflow-y-auto rounded border border-white/10 bg-white/[0.02]">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-white/10 hover:bg-transparent">
                        <TableHead className="text-white/40">Time</TableHead>
                        <TableHead className="text-white/40">Action</TableHead>
                        <TableHead className="text-white/40">Phase</TableHead>
                        <TableHead className="text-white/40 text-right">Tokens</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(activityData.recent_logs || []).map((l, i) => (
                        <TableRow key={i} className="border-white/10">
                          <TableCell className="text-white/40 text-xs">{new Date(l.created_at).toLocaleString()}</TableCell>
                          <TableCell className="text-white/70 text-xs font-mono">{l.action}</TableCell>
                          <TableCell className="text-white/40 text-xs">{l.phase}</TableCell>
                          <TableCell className="text-amber-400 text-xs text-right">{l.tokens_spent}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Provider Dialog */}
      <Dialog open={showProviderDialog} onOpenChange={setShowProviderDialog}>
        <DialogContent className="bg-[#0a0a0a] border-white/10 text-white" data-testid="provider-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">{editingProvider ? 'Edit Provider' : 'Add Provider'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-white/60 text-sm">Name</Label>
              <Input value={providerForm.name} onChange={(e) => setProviderForm(f => ({...f, name: e.target.value}))} className="bg-black/50 border-white/10 text-white mt-1" data-testid="provider-name-input" />
            </div>
            <div>
              <Label className="text-white/60 text-sm">Provider Key</Label>
              <Input value={providerForm.provider_key} onChange={(e) => setProviderForm(f => ({...f, provider_key: e.target.value}))} className="bg-black/50 border-white/10 text-white mt-1" placeholder="openai, anthropic, gemini" data-testid="provider-key-input" />
            </div>
            <div>
              <Label className="text-white/60 text-sm">Models (comma-separated)</Label>
              <Input value={providerForm.models} onChange={(e) => setProviderForm(f => ({...f, models: e.target.value}))} className="bg-black/50 border-white/10 text-white mt-1" placeholder="gpt-5.2, gpt-4o" data-testid="provider-models-input" />
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={providerForm.is_active} onCheckedChange={(v) => setProviderForm(f => ({...f, is_active: v}))} data-testid="provider-active-switch" />
              <Label className="text-white/60 text-sm">Active</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="border-white/20 text-white" onClick={() => setShowProviderDialog(false)}>Cancel</Button>
            <Button className="bg-indigo-600 hover:bg-indigo-500 text-white" onClick={saveProvider} data-testid="save-provider-btn">
              <Save className="w-4 h-4 mr-1.5" /> Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rate Dialog */}
      <Dialog open={showRateDialog} onOpenChange={setShowRateDialog}>
        <DialogContent className="bg-[#0a0a0a] border-white/10 text-white" data-testid="rate-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">{editingRate ? 'Edit Rate' : 'Add Rate'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-white/60 text-sm">Action</Label>
              <Input value={rateForm.action} onChange={(e) => setRateForm(f => ({...f, action: e.target.value}))} className="bg-black/50 border-white/10 text-white mt-1" placeholder="chat_message, code_generation" data-testid="rate-action-input" />
            </div>
            <div>
              <Label className="text-white/60 text-sm">Token Cost</Label>
              <Input type="number" value={rateForm.tokens_cost} onChange={(e) => setRateForm(f => ({...f, tokens_cost: parseInt(e.target.value) || 0}))} className="bg-black/50 border-white/10 text-white mt-1" data-testid="rate-cost-input" />
            </div>
            <div>
              <Label className="text-white/60 text-sm">Description</Label>
              <Input value={rateForm.description} onChange={(e) => setRateForm(f => ({...f, description: e.target.value}))} className="bg-black/50 border-white/10 text-white mt-1" data-testid="rate-desc-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="border-white/20 text-white" onClick={() => setShowRateDialog(false)}>Cancel</Button>
            <Button className="bg-indigo-600 hover:bg-indigo-500 text-white" onClick={saveRate} data-testid="save-rate-btn">
              <Save className="w-4 h-4 mr-1.5" /> Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
